import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import axiosInstance from '../../api/axiosInstance'
import Swal from 'sweetalert2'
import { getCurrentUserId } from '../../utils/functions'
import { resetAgentStatus } from '../Chat/agentStatus'

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#96C5F7',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
  purple: '#7C5CBF',
  teal:   '#1e9690',
}

// module_name matches the DB value
const MODULES = [
  {
    label:       'Vitals',
    module_name: 'force_vitals',
    icon:        'ti-activity',
    color:       '#9E2B25',
    bg:          '#9E2B2515',
    subtitle:    'Monitor and record patient vital signs',
  },
  {
    label:       'Force Scheduling',
    module_name: 'force_schedule',
    icon:        'ti-calendar-check',
    color:       '#2CA87F',
    bg:          '#2CA87F15',
    subtitle:    'Require doctors to have a schedule before appointments can be booked',
  },
  {
    label:       'Force Consents',
    module_name: 'force_consents',
    icon:        'ti-writing',
    color:       '#7C5CBF',
    bg:          '#7C5CBF15',
    subtitle:    'Require patients to sign linked consents before a procedure is performed',
  },
  {
    label:       'Dental Module',
    module_name: 'dental_module',
    icon:        'ti-tooth',
    color:       '#96C5F7',
    bg:          '#96C5F718',
    subtitle:    'Dental charting and treatment planning',
  },
  {
    label:       'Physiotherapy',
    module_name: 'physiotherapy',
    icon:        'ti-walk',
    color:       '#2CA87F',
    bg:          '#2CA87F15',
    subtitle:    'Rehabilitation and therapy session management',
  },
  {
    label:       'Psychology',
    module_name: 'psychology',
    icon:        'ti-brain',
    color:       '#7C5CBF',
    bg:          '#7C5CBF15',
    subtitle:    'Mental health assessments and session notes',
  },
  {
    label:       'Dermatology',
    module_name: 'dermatology',
    icon:        'ti-user-scan',
    color:       '#F29E4C',
    bg:          '#F29E4C18',
    subtitle:    'Skin condition tracking and treatment records',
  },
  {
    label:       'Insurance Module',
    module_name: 'insurance_module',
    icon:        'ti-shield-check',
    color:       '#1e9690',
    bg:          '#1e969015',
    subtitle:    'Insurance claims, approvals and eligibility',
  },
  {
    label:       'Loyalty System',
    module_name: 'loyalty_system',
    icon:        'ti-award',
    color:       '#F29E4C',
    bg:          '#F29E4C18',
    subtitle:    'Patient loyalty points, rewards and redemption',
  },
  {
    label:       'Advanced Inventory',
    module_name: 'advanced_inventory',
    icon:        'ti-packages',
    color:       '#1e9690',
    bg:          '#1e969015',
    subtitle:    'Advanced inventory tracking and stock management',
  },
  {
    label:       'HR System',
    module_name: 'hr_system',
    icon:        'ti-users-group',
    color:       '#7C5CBF',
    bg:          '#7C5CBF15',
    subtitle:    'Staff management, attendance and HR workflows',
  },
  {
    label:       'Assistant Agent',
    module_name: 'ai_agent',
    icon:        'ti-robot',
    color:       '#4680ff',
    bg:          '#4680ff15',
    subtitle:    'In-chat assistant that looks up schedules and patient files (read-only)',
  },
]

// ─── Reusable checkbox row ────────────────────────────────────────────────────

function OptionRow({ checked, onChange, label, description, color }) {
  return (
    <div
      onClick={onChange}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
        border: `1.5px solid ${checked ? color + '66' : '#e8ecf0'}`,
        background: checked ? color + '08' : '#fafbfc',
        transition: 'all 0.15s',
      }}
    >
      <div style={{
        marginTop: 1, width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `2px solid ${checked ? color : '#c8d0da'}`,
        background: checked ? color : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}>
        {checked && <i className="ti ti-check" style={{ color: '#fff', fontSize: 11 }} />}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 12, color: COLORS.gray, lineHeight: 1.5 }}>{description}</div>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const MedicalSafety = () => {
  const navigate = useNavigate()
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings)

  const [activeModal, setActiveModal]   = useState(null)
  const [forceVitals, setForceVitals]       = useState(false)
  const [forceSchedule, setForceSchedule]   = useState(false)
  const [forceConsents, setForceConsents]   = useState(false)

  // ── Restricted Files state ──────────────────────────────────────────────────
  const [restrictedFiles, setRestrictedFiles] = useState(false)
  const [rfModalOpen, setRfModalOpen]         = useState(false)
  const [rfSaving, setRfSaving]               = useState(false)

  // ── File Prefix state ────────────────────────────────────────────────────────
  const [fpOpen, setFpOpen]       = useState(false)
  const [fpPrefix, setFpPrefix]   = useState('P')
  const [fpDigits, setFpDigits]   = useState(5)
  const [fpSaved, setFpSaved]     = useState({ prefix: 'P', digits: 5 })
  const [fpSaving, setFpSaving]   = useState(false)

  // ── Appointment Views state ──────────────────────────────────────────────────
  const [avOpen, setAvOpen]               = useState(false)
  const [avUsers, setAvUsers]             = useState([])
  const [avDoctors, setAvDoctors]         = useState([])
  const [avSelectedUser, setAvSelectedUser] = useState(null)
  const [avAssignment, setAvAssignment]   = useState([])   // doctor IDs for selected user
  const [avLoading, setAvLoading]         = useState(false)
  const [avUserLoading, setAvUserLoading] = useState(false)
  const [avSaving, setAvSaving]           = useState(false)
  const [avSearch, setAvSearch]           = useState('')
  const [avRoleFilter, setAvRoleFilter]   = useState('all')
  const [dentalDiagramMode, setDentalDiagramMode] = useState(() => {
    const bs = JSON.parse(localStorage.getItem('businessSetting') || '{}')
    return bs?.dental_diagram_mode || 'universal'
  })
  const [enabled, setEnabled]           = useState({
    dental_module:       false,
    physiotherapy:       false,
    psychology:          false,
    dermatology:         false,
    insurance_module:    false,
    loyalty_system:      false,
    advanced_inventory:  false,
    hr_system:           false,
    ai_agent:            false,
  })
  const [loadingPage, setLoadingPage]   = useState(true)
  const [saving, setSaving]             = useState(false)

  // ── Branch Transactions state ─────────────────────────────────────────────
  const [btOpen, setBtOpen]           = useState(false)
  const [btBranchId, setBtBranchId]   = useState('')
  const [btLoading, setBtLoading]     = useState(false)
  const [btResult, setBtResult]       = useState(null)

  // ── Fetch current settings on mount ────────────────────────────────────────
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [modRes, prefixRes] = await Promise.all([
          axiosInstance.get('getModuleSettings'),
          axiosInstance.get('getFilePrefixSetting'),
        ])
        if (modRes.data?.success) {
          const rows = modRes.data.data
          const updated = { ...enabled }
          rows.forEach(row => {
            if (row.module_name === 'force_vitals') {
              setForceVitals(Boolean(row.enable))
            } else if (row.module_name === 'force_schedule') {
              setForceSchedule(Boolean(row.enable))
            } else if (row.module_name === 'force_consents') {
              setForceConsents(Boolean(row.enable))
            } else if (row.module_name === 'restricted_files') {
              setRestrictedFiles(Boolean(row.enable))
            } else if (updated.hasOwnProperty(row.module_name)) {
              updated[row.module_name] = Boolean(row.enable)
            }
          })
          setEnabled(updated)
        }
        if (prefixRes.data?.success && prefixRes.data.data) {
          const { prefix, digits } = prefixRes.data.data
          setFpPrefix(prefix)
          setFpDigits(digits)
          setFpSaved({ prefix, digits })
        }
      } catch (err) {
        console.error('Failed to fetch module settings', err)
      } finally {
        setLoadingPage(false)
      }
    }
    fetchSettings()
  }, [])

  // ── Save handler ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const activeMod = MODULES.find(m => m.label === activeModal)
    if (!activeMod) return

    const enableValue = activeMod.module_name === 'force_vitals'
      ? forceVitals
      : activeMod.module_name === 'force_schedule'
        ? forceSchedule
        : activeMod.module_name === 'force_consents'
          ? forceConsents
          : enabled[activeMod.module_name]

    setSaving(true)
    try {
      const res = await axiosInstance.put('updateModuleSetting', {
        module_name: activeMod.module_name,
        enable:      enableValue,
      })
      if (res.data?.success) {
        // Also save dental diagram mode when saving Dental Module
        if (activeMod.module_name === 'dental_module') {
          const doctorId = getCurrentUserId()
          if (doctorId) {
            await axiosInstance.put(`updateBusinessSetting/${doctorId}`, { dental_diagram_mode: dentalDiagramMode })
          }
        }
        // Let the chat dock re-probe instead of holding its memoised answer for
        // the rest of the session. The server's own flag cache still lapses on
        // its own within a minute.
        if (activeMod.module_name === 'ai_agent') resetAgentStatus()
        setActiveModal(null)
        Swal.fire({ icon: 'success', title: 'Saved', text: `${activeMod.label} setting updated.`, timer: 1500, showConfirmButton: false })
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to save', 'error')
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to save setting', 'error')
    } finally {
      setSaving(false)
    }
  }

  // ── Restricted Files handlers ────────────────────────────────────────────────
  const saveRestrictedFiles = async (value) => {
    setRfSaving(true)
    try {
      const res = await axiosInstance.put('updateModuleSetting', { module_name: 'restricted_files', enable: value })
      if (res.data?.success) {
        setRestrictedFiles(value)
        Swal.fire({ icon: 'success', title: 'Saved', text: `File restriction ${value ? 'enabled' : 'disabled'}.`, timer: 1500, showConfirmButton: false })
        setRfModalOpen(false)
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to save', 'error')
      }
    } catch {
      Swal.fire('Error', 'Failed to save setting', 'error')
    } finally {
      setRfSaving(false)
    }
  }

  // ── File Prefix handlers ─────────────────────────────────────────────────────
  const openFpModal = () => {
    setFpPrefix(fpSaved.prefix)
    setFpDigits(fpSaved.digits)
    setFpOpen(true)
  }

  const saveFilePrefixSetting = async () => {
    const p = fpPrefix.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (p.length < 1 || p.length > 3) {
      Swal.fire('Invalid', 'Prefix must be 1–3 alphanumeric characters.', 'warning')
      return
    }
    setFpSaving(true)
    try {
      const res = await axiosInstance.put('updateFilePrefixSetting', { prefix: p, digits: fpDigits })
      if (res.data?.success) {
        setFpSaved({ prefix: p, digits: fpDigits })
        setFpPrefix(p)
        setFpOpen(false)
        Swal.fire({ icon: 'success', title: 'Saved', text: 'File prefix settings updated.', timer: 1500, showConfirmButton: false })
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to save', 'error')
      }
    } catch {
      Swal.fire('Error', 'Failed to save prefix settings', 'error')
    } finally {
      setFpSaving(false)
    }
  }

  // ── Appointment Views handlers ───────────────────────────────────────────────
  const openAvModal = async () => {
    setAvOpen(true)
    setAvSelectedUser(null)
    setAvAssignment([])
    setAvSearch('')
    setAvRoleFilter('all')
    setAvLoading(true)
    try {
      const [usersRes, doctorsRes] = await Promise.all([
        axiosInstance.get('getAllUsersForView'),
        axiosInstance.get('getActiveDoctors'),
      ])
      setAvUsers(usersRes.data?.success ? usersRes.data.data : [])
      setAvDoctors(doctorsRes.data?.success ? doctorsRes.data.data : [])
    } catch {
      Swal.fire('Error', 'Failed to load data', 'error')
    } finally {
      setAvLoading(false)
    }
  }

  const selectAvUser = async (user) => {
    setAvSelectedUser(user)
    setAvAssignment([])
    setAvUserLoading(true)
    try {
      const res = await axiosInstance.get(`getSelectedDoctors/${user.id}`)
      setAvAssignment((res.data?.selectedDoctors || []).map(String))
    } catch {
      setAvAssignment([])
    } finally {
      setAvUserLoading(false)
    }
  }

  const toggleAvDoctor = (id) => {
    const sid = String(id)
    setAvAssignment(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    )
  }

  const saveAvAssignment = async () => {
    if (!avSelectedUser) return
    if (!avSelectedUser.id) {
      Swal.fire('Cannot Save', 'This user has no associated profile record and cannot have a doctor view assigned.', 'warning')
      return
    }
    setAvSaving(true)
    try {
      await axiosInstance.post('saveSelectedDoctors', {
        doctor_id: avSelectedUser.id,
        selected_doctor_ids: avAssignment.join(','),
      })
      const label = avSelectedUser.displayName || avSelectedUser.firstName || 'User'
      const msg = avAssignment.length === 0
        ? `${label} will now see all doctors (no restriction).`
        : `${label} restricted to ${avAssignment.length} doctor${avAssignment.length !== 1 ? 's' : ''}.`
      Swal.fire({ icon: 'success', title: 'Saved', text: msg, timer: 1800, showConfirmButton: false })
    } catch {
      Swal.fire('Error', 'Failed to save assignment', 'error')
    } finally {
      setAvSaving(false)
    }
  }

  const toggleEnabled  = (module_name) => setEnabled(prev => ({ ...prev, [module_name]: !prev[module_name] }))
  const activeMod      = MODULES.find(m => m.label === activeModal)
  const closeModal     = () => { if (!saving) setActiveModal(null) }

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loadingPage) {
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <div className="spinner-border" role="status" style={{ color: COLORS.red }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              style={{
                width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
                background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, marginRight: 4, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
            >
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.red}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-briefcase-medical" style={{ color: COLORS.red, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Medical Safety</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage medical safety protocols and configurations</div>
            </div>
          </div>

          {/* ── Module Grid ── */}
          <div style={{ padding: '20px 20px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
              Modules
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {MODULES.map(mod => {
                const isEnabled = mod.module_name === 'force_vitals'
                  ? undefined
                  : mod.module_name === 'force_schedule'
                    ? forceSchedule
                    : mod.module_name === 'force_consents'
                      ? forceConsents
                      : enabled[mod.module_name]
                return (
                  <ModuleCard
                    key={mod.label}
                    mod={mod}
                    isEnabled={isEnabled}
                    onClick={() => setActiveModal(mod.label)}
                  />
                )
              })}
            </div>
          </div>

          {/* ── Access Control ── */}
          <div style={{ padding: '20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
              Access Control
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <AppointmentViewsCard onClick={openAvModal} />
              <RestrictedFilesCard enabled={restrictedFiles} onClick={() => setRfModalOpen(true)} />
              <FilePrefixCard prefix={fpSaved.prefix} digits={fpSaved.digits} onClick={openFpModal} />
              <BranchTransactionsCard onClick={() => { setBtResult(null); setBtBranchId(allBranchSettings?.[0]?.id ? String(allBranchSettings[0].id) : ''); setBtOpen(true); }} />
            </div>
          </div>

        </div>

        {/* ── Restricted Files Modal ── */}
        {rfModalOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => !rfSaving && setRfModalOpen(false)}>
            <div style={{ background: '#fff', borderRadius: 12, width: 420, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: '#F29E4C18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ti ti-file-lock" style={{ color: '#F29E4C', fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Restricted Files Settings</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>Control patient file access across the system</div>
                </div>
                <button onClick={() => setRfModalOpen(false)} disabled={rfSaving}
                  style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: rfSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                  <i className="ti ti-x" />
                </button>
              </div>

              <OptionRow
                checked={restrictedFiles}
                onChange={() => !rfSaving && setRestrictedFiles(v => !v)}
                label="Enable File Restriction"
                description="When enabled, patient files are restricted and access must be explicitly granted per user or role."
                color="#F29E4C"
              />

              <div style={{ borderTop: '1px solid #f0f2f5', margin: '20px 0 0' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button onClick={() => setRfModalOpen(false)} disabled={rfSaving}
                  style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: rfSaving ? 'not-allowed' : 'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => saveRestrictedFiles(restrictedFiles)} disabled={rfSaving}
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ padding: '7px 16px', fontSize: 13 }}>
                  {rfSaving && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                  {rfSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── File Prefix Modal ── */}
        {fpOpen && (() => {
          const cleanPrefix = fpPrefix.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3)
          const previewNum = (n) => `${cleanPrefix}${String(n).padStart(fpDigits, '0')}`
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => !fpSaving && setFpOpen(false)}>
              <div style={{ background: '#fff', borderRadius: 12, width: 460, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: '#4680ff15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ti ti-file-digit" style={{ color: '#4680ff', fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>File Prefix Settings</div>
                    <div style={{ fontSize: 11, color: COLORS.gray }}>Configure how patient file numbers are generated</div>
                  </div>
                  <button onClick={() => setFpOpen(false)} disabled={fpSaving}
                    style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: fpSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                    <i className="ti ti-x" />
                  </button>
                </div>

                {/* Prefix input */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                    File Prefix <span style={{ color: COLORS.red }}>*</span>
                  </label>
                  <input
                    maxLength={3}
                    value={fpPrefix}
                    onChange={e => setFpPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    placeholder="e.g. P or ABC"
                    style={{ width: '100%', height: 36, borderRadius: 8, border: '1px solid #dde3eb', padding: '0 12px', fontSize: 15, fontWeight: 700, letterSpacing: '0.1em', color: '#1a202c', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderColor = '#4680ff'}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'}
                  />
                  <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 4 }}>1–3 uppercase letters or digits (e.g. P, PAT, ABC)</div>
                </div>

                {/* Digits selector */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                    Number of Digits
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[3, 4, 5, 6].map(d => (
                      <button key={d} onClick={() => setFpDigits(d)}
                        style={{
                          flex: 1, height: 38, borderRadius: 8, border: `1.5px solid ${fpDigits === d ? '#4680ff' : '#dde3eb'}`,
                          background: fpDigits === d ? '#4680ff' : '#fafbfc',
                          color: fpDigits === d ? '#fff' : COLORS.gray,
                          fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all 0.12s',
                        }}>
                        {d}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 4 }}>Total digits in the numeric part after the prefix</div>
                </div>

                {/* Live preview */}
                <div style={{ background: '#f8f9fb', borderRadius: 8, border: '1px solid #e8ecf0', padding: '12px 14px', marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Preview</div>
                  {cleanPrefix.length > 0 ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {[1, 2, 3, 50, 100].map(n => (
                        <span key={n} style={{ padding: '3px 10px', borderRadius: 6, background: '#4680ff15', color: '#4680ff', fontWeight: 700, fontSize: 13, fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                          {previewNum(n)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>Enter a prefix to see preview</div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setFpOpen(false)} disabled={fpSaving}
                    style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: fpSaving ? 'not-allowed' : 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={saveFilePrefixSetting} disabled={fpSaving || cleanPrefix.length === 0}
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ padding: '7px 16px', fontSize: 13, opacity: cleanPrefix.length === 0 ? 0.5 : 1 }}>
                    {fpSaving && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                    {fpSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* ── Appointment Views Modal ── */}
        {avOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={() => !avSaving && setAvOpen(false)}>
            <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 780, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#4680ff15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ti ti-layout-columns" style={{ color: '#4680ff', fontSize: 17 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Appointment Views</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>Restrict which doctors each user can see in the appointment board</div>
                </div>
                <button onClick={() => setAvOpen(false)} disabled={avSaving}
                  style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}
                ><i className="ti ti-x" /></button>
              </div>

              {/* Body */}
              {avLoading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
                  <div className="spinner-border spinner-border-sm" style={{ color: '#4680ff' }} />
                  <span style={{ marginLeft: 10, fontSize: 13, color: COLORS.gray }}>Loading users and doctors…</span>
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                  {/* ── Left: User list ── */}
                  <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #e8ecf0', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
                    {/* Search + role filter */}
                    <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f0f2f5' }}>
                      <div style={{ position: 'relative', marginBottom: 8 }}>
                        <i className="ti ti-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                        <input
                          placeholder="Search user…"
                          value={avSearch}
                          onChange={e => setAvSearch(e.target.value)}
                          style={{ width: '100%', height: 30, fontSize: 12, border: '1px solid #d0d5dd', borderRadius: 7, paddingLeft: 26, paddingRight: 8, outline: 'none', background: '#fff', color: '#374151', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {['all', 'admin', 'doctor', 'nurse', 'receptionist'].map(r => (
                          <button key={r} onClick={() => setAvRoleFilter(r)}
                            style={{ padding: '2px 8px', borderRadius: 10, border: `1px solid ${avRoleFilter === r ? '#4680ff' : '#e8ecf0'}`, background: avRoleFilter === r ? '#4680ff' : '#fff', color: avRoleFilter === r ? '#fff' : COLORS.gray, fontSize: 10, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}
                          >{r === 'all' ? 'All' : r}</button>
                        ))}
                      </div>
                    </div>
                    {/* User rows */}
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {avUsers
                        .filter(u => {
                          const name = `${u.firstName || ''} ${u.lastName || ''} ${u.displayName || ''}`.toLowerCase()
                          const matchSearch = !avSearch || name.includes(avSearch.toLowerCase())
                          const matchRole = avRoleFilter === 'all' || (u.role || '').toLowerCase() === avRoleFilter
                          return matchSearch && matchRole
                        })
                        .map(user => {
                          const name = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'
                          const role = (user.role || '').toLowerCase()
                          const roleColor = { admin: COLORS.red, doctor: COLORS.green, nurse: COLORS.orange, receptionist: COLORS.blue }[role] || COLORS.gray
                          const isSelected = avSelectedUser?.id === user.id
                          const noProfile = !user.id
                          return (
                            <div key={user.userId || user.email} onClick={() => !noProfile && selectAvUser(user)}
                              style={{ padding: '9px 12px', cursor: noProfile ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f0f2f5', background: isSelected ? '#4680ff0d' : 'transparent', borderLeft: `3px solid ${isSelected ? '#4680ff' : 'transparent'}`, transition: 'all 0.12s', opacity: noProfile ? 0.45 : 1 }}
                              onMouseEnter={e => { if (!isSelected && !noProfile) e.currentTarget.style.background = '#f4f7ff' }}
                              onMouseLeave={e => { if (!isSelected && !noProfile) e.currentTarget.style.background = 'transparent' }}
                              title={noProfile ? 'No profile record — cannot assign doctors' : ''}
                            >
                              <div style={{ fontSize: 12, fontWeight: isSelected ? 700 : 600, color: isSelected ? '#4680ff' : '#1a202c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: roleColor + '18', color: roleColor, textTransform: 'capitalize' }}>{user.role || '—'}</span>
                            </div>
                          )
                        })}
                    </div>
                  </div>

                  {/* ── Right: Doctor assignment ── */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {!avSelectedUser ? (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', gap: 10 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f8f9fb', border: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="ti ti-user-search" style={{ fontSize: 22, color: '#d1d5db' }} />
                        </div>
                        <div style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500 }}>Select a user to assign doctors</div>
                      </div>
                    ) : (
                      <>
                        {/* Selected user header */}
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#4680ff15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="ti ti-user" style={{ color: '#4680ff', fontSize: 15 }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>
                              {avSelectedUser.displayName || `${avSelectedUser.firstName || ''} ${avSelectedUser.lastName || ''}`.trim()}
                            </div>
                            <div style={{ fontSize: 11, color: COLORS.gray, textTransform: 'capitalize' }}>{avSelectedUser.role}</div>
                          </div>
                          <div style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.gray }}>
                            {avAssignment.length === 0 ? 'All doctors visible' : `${avAssignment.length} doctor${avAssignment.length !== 1 ? 's' : ''} assigned`}
                          </div>
                        </div>

                        {avUserLoading ? (
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="spinner-border spinner-border-sm" style={{ color: '#4680ff' }} />
                          </div>
                        ) : (
                          <>
                            {/* Info bar + select all */}
                            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: 8, background: '#f8f9fb', flexShrink: 0 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                <div
                                  onClick={() => setAvAssignment(prev => prev.length === avDoctors.length ? [] : avDoctors.map(d => String(d.id)))}
                                  style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${avAssignment.length === avDoctors.length && avDoctors.length > 0 ? '#4680ff' : '#c8d0da'}`, background: avAssignment.length === avDoctors.length && avDoctors.length > 0 ? '#4680ff' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                  {avAssignment.length === avDoctors.length && avDoctors.length > 0 && <i className="ti ti-check" style={{ color: '#fff', fontSize: 10 }} />}
                                </div>
                                Select All
                              </label>
                              <span style={{ fontSize: 11, color: COLORS.gray, marginLeft: 'auto' }}>
                                {avAssignment.length === 0 ? <span style={{ color: '#f39c12', fontWeight: 600 }}>⚠ No restriction — user sees all doctors</span> : `Restricted to ${avAssignment.length} / ${avDoctors.length}`}
                              </span>
                            </div>

                            {/* Doctor list */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {avDoctors.map(doc => {
                                const checked = avAssignment.includes(String(doc.id))
                                return (
                                  <div key={doc.id} onClick={() => toggleAvDoctor(doc.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', border: `1.5px solid ${checked ? COLORS.green + '66' : '#e8ecf0'}`, background: checked ? COLORS.green + '08' : '#fafbfc', transition: 'all 0.12s' }}
                                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f0faf6' }}
                                    onMouseLeave={e => { if (!checked) e.currentTarget.style.background = '#fafbfc' }}
                                  >
                                    <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? COLORS.green : '#c8d0da'}`, background: checked ? COLORS.green : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.12s' }}>
                                      {checked && <i className="ti ti-check" style={{ color: '#fff', fontSize: 10 }} />}
                                    </div>
                                    <i className="ti ti-stethoscope" style={{ fontSize: 14, color: checked ? COLORS.green : '#9CA3AF', flexShrink: 0 }} />
                                    <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? '#1a202c' : '#374151' }}>{doc.displayName}</span>
                                  </div>
                                )
                              })}
                            </div>

                            {/* Save footer */}
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #e8ecf0', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0, background: '#fafbfc' }}>
                              <button onClick={() => { setAvSelectedUser(null); setAvAssignment([]) }}
                                style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>
                                Clear
                              </button>
                              <button onClick={saveAvAssignment} disabled={avSaving}
                                className="bgBtn"
                                style={{ padding: '7px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                {avSaving && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                                {avSaving ? 'Saving…' : 'Save Assignment'}
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Shared Modal ── */}
        {activeModal && activeMod && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={closeModal}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: 420, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: activeMod.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`ti ${activeMod.icon}`} style={{ color: activeMod.color, fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>{activeMod.label} Settings</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>{activeMod.subtitle}</div>
                </div>
                <button
                  onClick={closeModal}
                  disabled={saving}
                  style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>

              {/* Vitals: Force Vitals toggle */}
              {activeModal === 'Vitals' && (
                <OptionRow
                  checked={forceVitals}
                  onChange={() => setForceVitals(v => !v)}
                  label="Force Vitals"
                  description="Enabling this option will force practitioners to enter vitals before treatment."
                  color={activeMod.color}
                />
              )}

              {/* Force Scheduling toggle */}
              {activeModal === 'Force Scheduling' && (
                <OptionRow
                  checked={forceSchedule}
                  onChange={() => setForceSchedule(v => !v)}
                  label="Force Scheduling"
                  description="When enabled, appointments can only be booked on dates where the doctor has an active schedule set."
                  color={activeMod.color}
                />
              )}

              {/* Force Consents toggle */}
              {activeModal === 'Force Consents' && (
                <OptionRow
                  checked={forceConsents}
                  onChange={() => setForceConsents(v => !v)}
                  label="Force Consents"
                  description="When enabled, patients must sign all consents linked to a procedure before it can be performed."
                  color={activeMod.color}
                />
              )}

              {/* All other modules: Enable toggle */}
              {activeModal !== 'Vitals' && activeModal !== 'Force Scheduling' && activeModal !== 'Force Consents' && (
                <OptionRow
                  checked={enabled[activeMod.module_name]}
                  onChange={() => toggleEnabled(activeMod.module_name)}
                  label={`Enable ${activeMod.label}`}
                  description={`Enable the ${activeMod.label} module to make it available across the system.`}
                  color={activeMod.color}
                />
              )}

              {/* Dental Module: Diagram Mode */}
              {activeModal === 'Dental Module' && (
                <div style={{ marginTop: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                    Dental Diagram Mode
                  </label>
                  <select
                    value={dentalDiagramMode}
                    onChange={e => setDentalDiagramMode(e.target.value)}
                    style={{
                      width: '100%', height: 36, borderRadius: 8, border: '1px solid #dde3eb',
                      padding: '0 12px', fontSize: 13, color: '#1a202c', outline: 'none',
                      background: '#fff',
                    }}
                    onFocus={e => e.target.style.borderColor = activeMod.color}
                    onBlur={e => e.target.style.borderColor = '#dde3eb'}
                  >
                    <option value="universal">Universal</option>
                    <option value="fdi">FDI</option>
                  </select>
                  <div style={{ fontSize: 11, color: COLORS.gray, marginTop: 5 }}>
                    Choose the tooth numbering system used in dental charts.
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid #f0f2f5', margin: '20px 0 0' }} />

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button
                  onClick={closeModal}
                  disabled={saving}
                  style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ padding: '7px 16px', fontSize: 13 }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving && <i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />}
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Branch Transactions Modal ── */}
        {btOpen && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => !btLoading && setBtOpen(false)}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: 460, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#7C5CBF18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ti ti-git-branch" style={{ color: '#7C5CBF', fontSize: 20 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Branch Transactions</div>
                  <div style={{ fontSize: 11, color: COLORS.gray }}>Assign untagged historical records to a branch</div>
                </div>
              </div>

              {/* Warning */}
              <div style={{ background: '#fff8e1', border: '1px solid #f59e0b44', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
                This will assign all records with <strong>no branch</strong> (appointments, invoices, payments, services, pharmacy, labs, prescriptions, certificates, inventory items) to the selected branch. This action cannot be undone.
              </div>

              {/* Branch Select */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray, display: 'block', marginBottom: 6 }}>
                  Select Branch
                </label>
                <select
                  value={btBranchId}
                  onChange={e => setBtBranchId(e.target.value)}
                  disabled={btLoading}
                  style={{ width: '100%', height: 38, borderRadius: 8, border: '1px solid #dde3eb', padding: '0 12px', fontSize: 13, color: '#1a202c', outline: 'none', background: '#fff' }}
                >
                  <option value="">— Select a branch —</option>
                  {(allBranchSettings || []).map((b, i) => (
                    <option key={b.id} value={b.id}>
                      Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Result summary */}
              {btResult && (
                <div style={{ background: '#f0fdf4', border: '1px solid #22c55e44', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: '#15803d', marginBottom: 8 }}>
                    <i className="ti ti-circle-check" style={{ marginRight: 6 }} />
                    {btResult.message}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', color: '#555' }}>
                    {Object.entries(btResult.data || {}).map(([table, count]) => (
                      <div key={table} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ textTransform: 'capitalize' }}>{table.replace(/_/g, ' ')}</span>
                        <span style={{ fontWeight: 600, color: '#15803d' }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assign button */}
              <div style={{ marginBottom: 16 }}>
                <button
                  disabled={!btBranchId || btLoading}
                  onClick={async () => {
                    const confirm = await Swal.fire({
                      title: 'Assign null transactions?',
                      text: 'All records with no branch tag will be assigned to the selected branch. This cannot be undone.',
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonText: 'Yes, assign',
                      cancelButtonText: 'Cancel',
                    })
                    if (!confirm.isConfirmed) return
                    try {
                      setBtLoading(true)
                      setBtResult(null)
                      const res = await axiosInstance.post('assignNullBranchTransactions', { branch_id: parseInt(btBranchId) })
                      setBtResult(res.data)
                    } catch (e) {
                      Swal.fire('Error', e.response?.data?.message || 'Failed to assign branch', 'error')
                    } finally {
                      setBtLoading(false)
                    }
                  }}
                  style={{
                    width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                    background: !btBranchId || btLoading ? '#e2e8f0' : '#7C5CBF',
                    color: !btBranchId || btLoading ? '#94a3b8' : '#fff',
                    fontSize: 13, fontWeight: 600,
                    cursor: !btBranchId || btLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {btLoading
                    ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Assigning…</>
                    : <><i className="ti ti-git-merge" /> Assign Null Transactions</>}
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setBtOpen(false)}
                  disabled={btLoading}
                  style={{ padding: '7px 20px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: btLoading ? 'not-allowed' : 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Module card ──────────────────────────────────────────────────────────────

function ModuleCard({ mod, onClick, isEnabled }) {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
        background: hovered ? mod.bg : '#fff',
        border: '1px solid', borderColor: hovered ? mod.color + '55' : '#e8ecf0',
        borderLeft: `4px solid ${mod.color}`,
        borderRadius: 10, width: '100%',
        transition: 'all 0.15s',
        boxShadow: hovered ? `0 2px 8px ${mod.color}18` : 'none',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: mod.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'transform 0.15s',
        transform: hovered ? 'scale(1.08)' : 'scale(1)',
      }}>
        <i className={`ti ${mod.icon}`} style={{ color: mod.color, fontSize: 20 }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>{mod.label}</div>
        <div style={{ fontSize: 11, color: COLORS.gray, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {mod.subtitle}
        </div>
      </div>

      {isEnabled !== undefined && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, flexShrink: 0,
          background: isEnabled ? `${mod.color}18` : '#f0f2f5',
          color: isEnabled ? mod.color : COLORS.gray,
          whiteSpace: 'nowrap',
        }}>
          {isEnabled ? 'Enabled' : 'Disabled'}
        </span>
      )}

      <i className="ti ti-chevron-right" style={{ color: hovered ? mod.color : '#c8d0da', fontSize: 16, flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  )
}

// ─── Appointment Views card ───────────────────────────────────────────────────

function AppointmentViewsCard({ onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
        background: hovered ? '#4680ff0d' : '#fff',
        border: '1px solid', borderColor: hovered ? '#4680ff55' : '#e8ecf0',
        borderLeft: '4px solid #4680ff',
        borderRadius: 10, width: '100%', maxWidth: 340,
        transition: 'all 0.15s',
        boxShadow: hovered ? '0 2px 8px #4680ff18' : 'none',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: '#4680ff15', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: hovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s',
      }}>
        <i className="ti ti-layout-columns" style={{ color: '#4680ff', fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>Appointment Views</div>
        <div style={{ fontSize: 11, color: COLORS.gray, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Restrict which doctors each user sees in the appointment board
        </div>
      </div>
      <i className="ti ti-chevron-right" style={{ color: hovered ? '#4680ff' : '#c8d0da', fontSize: 16, flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  )
}

// ─── File Prefix card ─────────────────────────────────────────────────────────

function FilePrefixCard({ prefix, digits, onClick }) {
  const [hovered, setHovered] = useState(false)
  const example = `${prefix}${'0'.repeat(digits - 1)}1`
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
        background: hovered ? '#4680ff0d' : '#fff',
        border: '1px solid', borderColor: hovered ? '#4680ff55' : '#e8ecf0',
        borderLeft: '4px solid #4680ff',
        borderRadius: 10, width: '100%', maxWidth: 340,
        transition: 'all 0.15s',
        boxShadow: hovered ? '0 2px 8px #4680ff18' : 'none',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: '#4680ff15', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: hovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s',
      }}>
        <i className="ti ti-file-digit" style={{ color: '#4680ff', fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>File Prefix</div>
        <div style={{ fontSize: 11, color: COLORS.gray, lineHeight: 1.4 }}>
          Current format: <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#4680ff' }}>{example}</span>, <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#4680ff' }}>{prefix}{'0'.repeat(digits - 1)}2</span>…
        </div>
      </div>
      <i className="ti ti-chevron-right" style={{ color: hovered ? '#4680ff' : '#c8d0da', fontSize: 16, flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  )
}

// ─── Restricted Files card ────────────────────────────────────────────────────

function RestrictedFilesCard({ enabled, onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
        background: hovered ? '#F29E4C0d' : '#fff',
        border: '1px solid', borderColor: hovered ? '#F29E4C55' : '#e8ecf0',
        borderLeft: '4px solid #F29E4C',
        borderRadius: 10, width: '100%', maxWidth: 340,
        transition: 'all 0.15s',
        boxShadow: hovered ? '0 2px 8px #F29E4C18' : 'none',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: '#F29E4C18', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: hovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s',
      }}>
        <i className="ti ti-file-lock" style={{ color: '#F29E4C', fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>Restricted Files</div>
        <div style={{ fontSize: 11, color: COLORS.gray, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Control patient file access across the system
        </div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, flexShrink: 0,
        background: enabled ? '#F29E4C18' : '#f0f2f5',
        color: enabled ? '#F29E4C' : COLORS.gray,
        whiteSpace: 'nowrap',
      }}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
      <i className="ti ti-chevron-right" style={{ color: hovered ? '#F29E4C' : '#c8d0da', fontSize: 16, flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  )
}

// ─── Branch Transactions card ─────────────────────────────────────────────────

function BranchTransactionsCard({ onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
        background: hovered ? '#7C5CBF0d' : '#fff',
        border: '1px solid', borderColor: hovered ? '#7C5CBF55' : '#e8ecf0',
        borderLeft: '4px solid #7C5CBF',
        borderRadius: 10, width: '100%', maxWidth: 340,
        transition: 'all 0.15s',
        boxShadow: hovered ? '0 2px 8px #7C5CBF18' : 'none',
      }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 10, flexShrink: 0,
        background: '#7C5CBF18', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: hovered ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s',
      }}>
        <i className="ti ti-git-branch" style={{ color: '#7C5CBF', fontSize: 20 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>Branch Transactions</div>
        <div style={{ fontSize: 11, color: COLORS.gray, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Assign old records to a branch in bulk
        </div>
      </div>
      <i className="ti ti-chevron-right" style={{ color: hovered ? '#7C5CBF' : '#c8d0da', fontSize: 16, flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  )
}

export default MedicalSafety
